import _ from "lodash";
import { Fragment, SyntheticEvent, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { createUseStyles } from "react-jss";
import { InlineIcon } from "@iconify/react";
import { Formik, Form, FieldArray, Field } from "formik";
import { StyleTypeContext, TranslationContext } from "../../../umbilical_tools";
import { useContext } from "react";
import { cStyler } from "../../../helpers";
import jssVariables from "../../../jss_setup";
import { modalGenericStyles } from "../../../jss_setup/modals";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "../../common/Button";
import tinycolor from "tinycolor2";
import {
  accSearchFormChange,
  accSearchFormUpdate,
} from "../../../actions/search/accommodation_search";
import { NoScrollContext, brand } from "../../../context";

function usePrepareData() {
  const { t } = useContext(TranslationContext);
  const { type, brand } = useContext(StyleTypeContext);

  const dispatch = useDispatch();

  const classes = paxPaxPickerModalACCStyles({
    customStyles: cStyler({
      type,
      brand,
      objName: "paxModalCustomStyles",
    }),
  });

  const { max_rooms_allowed, search_form, search_form_from_meta } = useSelector(
    (state) => {
      return {
        max_rooms_allowed:
          state.WebsiteMetadata?.metadata.max_rooms_allowed ?? 4,
        search_form: state.HotelSearchForm,
        search_form_from_meta: state.HotelSearch.searchData,
      };
    },
  );

  const initialValues = { rooms: search_form.rooms };

  const onFormChange = useCallback(
    (rooms) => dispatch(accSearchFormChange("rooms", rooms)),
    [dispatch],
  );

  const onFormUpdate = useCallback(
    (form) => dispatch(accSearchFormUpdate({ form })),
    [dispatch],
  );

  useEffect(() => {
    if (_.isEmpty(search_form_from_meta) || search_form.destination_code)
      return;
    onFormUpdate(search_form_from_meta);
  }, [search_form_from_meta]);

  return {
    t,
    classes,
    initialValues,
    max_rooms_allowed,
    onFormChange,
  };
}

function groupCommonStyles() {
  return {
    display: "grid",
    gridTemplateColumns: "max-content 1fr max-content",
    placeItems: "center",
    placeContent: "center",
    fontSize: "large",
    fontWeight: 500,
    padding: jssVariables.half_gap,
    border: `1px solid ${jssVariables.colors.base_color}`,
    color: jssVariables.colors.base_color,
    borderRadius: jssVariables.half_gap,
  };
}

function btnCommonStyles() {
  return {
    borderRadius: "50%",
    height: "2rem",
    width: "2rem",
    fontSize: "x-large",
    display: "inline-grid",
    placeItems: "center",
    padding: 0,
    background: "none",
    border: `1px solid ${jssVariables.colors.base_color}`,
  };
}

const roomsNumberControllerStyles = createUseStyles({
  roomsGroup: (props) => ({
    ...groupCommonStyles(),
    border: "none",
    background: jssVariables.colors.base_color,
    color: "white",
    ...(props?.customStyles?.roomsGroup ?? {}),
  }),
  roomsMinusBtn: (props) => ({
    ...btnCommonStyles(),
    borderColor: "white",
    ...(props?.customStyles?.roomsMinusBtn ?? {}),
  }),
  roomsPlusBtn: (props) => ({
    ...btnCommonStyles(),
    background: "white",
    border: "none",
    ...(props?.customStyles?.roomsPlusBtn ?? {}),
  }),
  roomsPlusBtnIcon: (props) => ({
    color: jssVariables.colors.base_color,
    ...(props?.customStyles?.roomsPlusBtnIcon ?? {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    roomsGroup: (props) => ({
      minWidth: "20rem",
      ...(props?.customStyles?.roomsGroup?.smallscreen ?? {}),
    }),
  },
});
export function RoomsNumberController({
  max_rooms_allowed = 3,
  rooms = [],
  roomTemplate,
  onRemove,
  onAdd,
}) {
  const { t } = useContext(TranslationContext);
  const { type } = useContext(StyleTypeContext);

  const customStyles = cStyler({
    type,
    brand,
    objName: "paxModalCustomStyles",
  });
  const classes = roomsNumberControllerStyles({ customStyles });

  return (
    <div className={classes.roomsGroup}>
      <button
        type="button"
        className={classes.roomsMinusBtn}
        id="roomsMinusBtn"
        onClick={() => {
          if (rooms.length > 1) {
            onRemove(rooms.length - 1);
          }
        }}
      >
        <InlineIcon icon="ic:outline-minus" />
      </button>
      {t("rooms")}: {rooms.length}
      <button
        type="button"
        className={classes.roomsPlusBtn}
        id="roomsPlusBtn"
        onClick={() => {
          if (rooms.length < max_rooms_allowed) {
            onAdd(roomTemplate);
          }
        }}
      >
        <InlineIcon
          icon="ic:outline-plus"
          className={classes.roomsPlusBtnIcon}
        />
      </button>
    </div>
  );
}
RoomsNumberController.defaultProps = {
  max_rooms_allowed: 3,
  rooms: [],
};
RoomsNumberController.propTypes = {
  max_rooms_allowed: PropTypes.number.isRequired,
  rooms: PropTypes.array.isRequired,
  roomTemplate: PropTypes.object.isRequired,
  onRemove: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
};

const roomControllerStyles = createUseStyles({
  body: (props) => ({
    ...modalGenericStyles.cardBody,
    gridGap: jssVariables.half_gap,
    padding: 0,
    ...(props?.customStyles?.body ?? {}),
  }),
  groups: (props) => ({
    display: "grid",
    gridGap: jssVariables.double_gap,
    maxHeight: "55vh",
    overflow: "auto",
    ...(props?.customStyles?.groups ?? {}),
  }),
  paxGroup: (props) => ({
    ...groupCommonStyles(),
    ...(props?.customStyles?.paxGroup ?? {}),
  }),
  roomNumTitle: (props) => ({
    fontSize: "large",
    color: "#7D7D7D",
    textTransform: "capitalize",
    textAlign: "start",
    lineHeight: "normal",
    ...(props?.customStyles?.roomNumTitle ?? {}),
  }),
  paxMinusBtn: (props) => ({
    ...btnCommonStyles(),
    background: "white",
    ...(props?.customStyles?.paxMinusBtn ?? {}),
  }),
  paxPlusBtn: (props) => ({
    ...btnCommonStyles(),
    background: jssVariables.colors.base_color,
    color: "white",
    ...(props?.customStyles?.paxPlusBtn ?? {}),
  }),
  ages: (props) => ({
    display: "grid",
    borderRadius: [0, 0, jssVariables.half_gap, jssVariables.half_gap],
    fontSize: "large",
    maxHeight: "18rem",
    overflow: "auto",
    margin: [0, jssVariables.half_gap],
    paddingTop: 0,
    background: tinycolor(jssVariables.colors.base_color)
      .lighten(45)
      .toRgbString(),
    ...(props?.customStyles?.ages ?? {}),
  }),
  ageItem: (props) => ({
    display: "grid",
    gridTemplateColumns: "1fr max-content",
    gridGap: `0 ${jssVariables.half_gap})`,
    alignItems: "center",
    textAlign: "left",
    gridAutoRows: "max-content",
    color: jssVariables.colors.base_color,
    padding: [jssVariables.half_gap, jssVariables.normal_gap],
    ...(props?.customStyles?.ageItem ?? {}),
  }),
  ageSelectField: (props) => ({
    border: `none!important`,
    background: tinycolor(jssVariables.colors.base_color)
      .lighten(45)
      .toRgbString(),
    appearance: "none",
    backgroundImage: `url("https://apollo-common-assets.s3.eu-west-3.amazonaws.com/images/down_select_arrow.svg")`,
    padding: `0 ${jssVariables.normal_gap}!important`,
    paddingRight: `calc(${jssVariables.normal_gap} * 1.5)!important`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right center",
    backgroundSize: `calc(${jssVariables.normal_gap} * 1.3)`,
    cursor: "pointer",
    "&:focus": {
      border: `none!important`,
    },
    ...(props?.customStyles?.ageSelectField ?? {}),
  }),
  ageItemsDivider: (props) => ({
    height: "1px",
    background: jssVariables.colors.base_color,
    gridColumn: "1 / -1",
    margin: `calc(${jssVariables.normal_gap} / 4) ${jssVariables.normal_gap}`,
    ...(props?.customStyles?.ageItemsDivider ?? {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    groups: (props) => ({
      gridTemplateColumns: "repeat(2, max-content)",
      gridGap: 0,
      maxHeight: "50vh",
      ...(props?.customStyles?.groups?.smallscreen ?? {}),
    }),
    body: (props) => ({
      minWidth: "20rem",
      alignContent: "baseline",
      gridGap: jssVariables.half_gap,
      margin: jssVariables.half_gap,
      ...(props?.customStyles?.body?.smallscreen ?? {}),
    }),
  },
});
export function RoomSetupController({
  rooms,
  extraCustomStyles = {},
  setFieldValue,
}) {
  const { t } = useContext(TranslationContext);
  const { type } = useContext(StyleTypeContext);
  const customStyles = cStyler({
    type,
    brand,
    objName: "paxModalCustomStyles",
  });

  const classes = roomControllerStyles({
    customStyles: { ...customStyles, ...extraCustomStyles },
  });
  return (
    <div className={classes.groups}>
      {rooms.map((__, idx) => (
        <div className={classes.body} key={idx}>
          <div className={classes.roomNumTitle}>
            {t("room")} {idx + 1}
          </div>
          <div className={classes.paxGroup}>
            <button
              type="button"
              className={classes.paxMinusBtn}
              id={`paxMinusAdultsBtn${idx + 1}`}
              onClick={() => {
                if (rooms[idx].adults > 1) {
                  setFieldValue(`rooms.${idx}.adults`, rooms[idx].adults - 1);
                }
              }}
            >
              <InlineIcon icon="ic:outline-minus" />
            </button>
            {t("adults")}: {rooms[idx].adults}
            <button
              type="button"
              className={classes.paxPlusBtn}
              id={`paxPlusAdultsBtn${idx + 1}`}
              onClick={() => {
                const adtNo = rooms[idx].adults;
                const chdNo = rooms[idx].children;

                // Do not accept more than 6 adults
                if (adtNo >= 6) return;

                // Do not accept more than 9 people in total
                if (adtNo + chdNo < 9) {
                  setFieldValue(`rooms.${idx}.adults`, adtNo + 1);
                }
              }}
            >
              <InlineIcon icon="ic:outline-plus" />
            </button>
          </div>
          <div className={classes.paxGroupContr}>
            <div className={classes.paxGroup}>
              <button
                type="button"
                className={classes.paxMinusBtn}
                id={`paxMinusChildrenBtn${idx + 1}`}
                onClick={() => {
                  if (rooms[idx].children > 0) {
                    const ages = [...rooms[idx].children_ages];
                    ages.pop();
                    setFieldValue(
                      `rooms.${idx}.children`,
                      rooms[idx].children - 1,
                    );
                    setFieldValue(`rooms.${idx}.children_ages`, ages);
                  }
                }}
              >
                <InlineIcon icon="ic:outline-minus" />
              </button>
              {t("children")}: {rooms[idx].children}
              <button
                type="button"
                className={classes.paxPlusBtn}
                id={`paxPlusChildrenBtn${idx + 1}`}
                onClick={() => {
                  const adtNo = rooms[idx].adults;
                  const chdNo = rooms[idx].children;
                  if (chdNo < 4 && adtNo + chdNo < 9) {
                    setFieldValue(`rooms.${idx}.children`, chdNo + 1);
                    setFieldValue(`rooms.${idx}.children_ages`, [
                      ...rooms[idx].children_ages,
                      "2",
                    ]);
                  }
                }}
              >
                <InlineIcon icon="ic:outline-plus" />
              </button>
            </div>
            <div className={classes.ages}>
              {rooms[idx].children_ages.map((__, index) => (
                <Fragment key={index}>
                  <div key={index} className={classes.ageItem}>
                    {t("child")} {index + 1}
                    <Field
                      className={classes.ageSelectField}
                      id="ageSelectField"
                      name={`rooms.${idx}.children_ages.${index}`}
                      as="select"
                    >
                      {new Array(13).fill(1).map((__, aidx) => (
                        <option value={aidx} key={aidx}>
                          {aidx} years old
                        </option>
                      ))}
                    </Field>
                  </div>
                  {index < rooms[idx].children_ages.length - 1 && (
                    <div className={classes.ageItemsDivider} />
                  )}
                </Fragment>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
RoomSetupController.defaultProps = {
  rooms: [],
  extraCustomStyles: {},
};
RoomSetupController.propTypes = {
  rooms: PropTypes.array.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  extraCustomStyles: PropTypes.object,
};

const paxPaxPickerModalACCStyles = createUseStyles({
  ...modalGenericStyles,
  PaxPickerModalACC: (props) => ({
    ...modalGenericStyles.modal,
    ...(props?.customStyles?.PaxPickerModalACC ?? {}),
  }),
  card: (props) => ({
    ...modalGenericStyles.card,
    width: "90vw",
    padding: jssVariables.double_gap,
    background: "#FAFAFD",
    top: `calc(${jssVariables.navbar_height} * 1.5)`,
    transform: "translateX(-50%)",
    ...(props?.customStyles?.card ?? {}),
  }),
  form: (props) => ({
    display: "grid",
    gridGap: jssVariables.double_gap,
    ...(props?.customStyles?.form ?? {}),
  }),
  body: (props) => ({
    ...modalGenericStyles.cardBody,
    gridGap: jssVariables.half_gap,
    padding: 0,
    ...(props?.customStyles?.body ?? {}),
  }),
  actions: (props) => ({
    ...modalGenericStyles.cardActions,
    padding: 0,
    gridGap: jssVariables.normal_gap,
    ...(props?.customStyles?.actions ?? {}),
  }),
  roomsGroup: (props) => ({
    ...groupCommonStyles(),
    border: "none",
    background: jssVariables.colors.base_color,
    color: "white",
    ...(props?.customStyles?.roomsGroup ?? {}),
  }),
  groups: (props) => ({
    display: "grid",
    gridGap: jssVariables.double_gap,
    maxHeight: "55vh",
    overflow: "auto",
    ...(props?.customStyles?.groups ?? {}),
  }),
  roomsMinusBtn: (props) => ({
    ...btnCommonStyles(),
    borderColor: "white",
    ...(props?.customStyles?.roomsMinusBtn ?? {}),
  }),
  roomsPlusBtn: (props) => ({
    ...btnCommonStyles(),
    background: "white",
    border: "none",
    ...(props?.customStyles?.roomsPlusBtn ?? {}),
  }),
  roomsPlusBtnIcon: (props) => ({
    color: jssVariables.colors.base_color,
    ...(props?.customStyles?.roomsPlusBtnIcon ?? {}),
  }),
  paxGroup: (props) => ({
    ...groupCommonStyles(),
    ...(props?.customStyles?.paxGroup ?? {}),
  }),
  roomNumTitle: (props) => ({
    fontSize: "large",
    color: "#7D7D7D",
    textTransform: "capitalize",
    textAlign: "start",
    lineHeight: "normal",
    ...(props?.customStyles?.roomNumTitle ?? {}),
  }),
  paxMinusBtn: (props) => ({
    ...btnCommonStyles(),
    background: "white",
    ...(props?.customStyles?.paxMinusBtn ?? {}),
  }),
  paxPlusBtn: (props) => ({
    ...btnCommonStyles(),
    background: jssVariables.colors.base_color,
    color: "white",
    ...(props?.customStyles?.paxPlusBtn ?? {}),
  }),
  ages: (props) => ({
    display: "grid",
    borderRadius: [0, 0, jssVariables.half_gap, jssVariables.half_gap],
    fontSize: "large",
    maxHeight: "18rem",
    overflow: "auto",
    margin: [0, jssVariables.half_gap],
    paddingTop: 0,
    background: tinycolor(jssVariables.colors.base_color)
      .lighten(45)
      .toRgbString(),
    ...(props?.customStyles?.ages ?? {}),
  }),
  ageItem: (props) => ({
    display: "grid",
    gridTemplateColumns: "1fr max-content",
    gridGap: `0 ${jssVariables.half_gap})`,
    alignItems: "center",
    textAlign: "left",
    gridAutoRows: "max-content",
    color: jssVariables.colors.base_color,
    padding: [jssVariables.half_gap, jssVariables.normal_gap],
    ...(props?.customStyles?.ageItem ?? {}),
  }),
  ageSelectField: (props) => ({
    border: `none!important`,
    background: tinycolor(jssVariables.colors.base_color)
      .lighten(45)
      .toRgbString(),
    appearance: "none",
    backgroundImage: `url("https://apollo-common-assets.s3.eu-west-3.amazonaws.com/images/down_select_arrow.svg")`,
    padding: `0 ${jssVariables.normal_gap}!important`,
    paddingRight: `calc(${jssVariables.normal_gap} * 1.5)!important`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right center",
    backgroundSize: `calc(${jssVariables.normal_gap} * 1.3)`,
    cursor: "pointer",
    "&:focus": {
      border: `none!important`,
    },
    ...(props?.customStyles?.ageSelectField ?? {}),
  }),
  ageItemsDivider: (props) => ({
    height: "1px",
    background: jssVariables.colors.base_color,
    gridColumn: "1 / -1",
    margin: `calc(${jssVariables.normal_gap} / 4) ${jssVariables.normal_gap}`,
    ...(props?.customStyles?.ageItemsDivider ?? {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    card: (props) => ({
      width: "max-content",
      top: `calc(${jssVariables.navbar_height} * 2)`,
      padding: [
        jssVariables.double_gap,
        `calc(${jssVariables.navbar_height} / 2)`,
      ],
      ...(props?.customStyles?.card?.smallscreen ?? {}),
    }),
    form: (props) => ({
      display: "grid",
      gridGap: jssVariables.double_gap,
      justifyItems: "center",
      ...(props?.customStyles?.form?.smallscreen ?? {}),
    }),
    roomsGroup: (props) => ({
      minWidth: "20rem",
      ...(props?.customStyles?.roomsGroup?.smallscreen ?? {}),
    }),
    groups: (props) => ({
      gridTemplateColumns: "repeat(2, max-content)",
      gridGap: 0,
      maxHeight: "50vh",
      ...(props?.customStyles?.groups?.smallscreen ?? {}),
    }),
    body: (props) => ({
      minWidth: "20rem",
      alignContent: "baseline",
      gridGap: jssVariables.half_gap,
      margin: jssVariables.half_gap,
      ...(props?.customStyles?.body?.smallscreen ?? {}),
    }),
    actions: (props) => ({
      width: "20rem",
      marginRight: jssVariables.half_gap,
      ...(props?.customStyles?.actions?.smallscreen ?? {}),
    }),
  },
});
function PaxPickerModalACC({ onCancel }) {
  const { toggleNoScroll } = useContext(NoScrollContext);

  const { classes, t, initialValues, onFormChange } = usePrepareData();
  const max_rooms_allowed = 3;

  useEffect(() => {
    toggleNoScroll(true);
    return () => toggleNoScroll(false);
  }, []);

  return (
    <div className={classes.PaxPickerModalACC}>
      <div className={classes.card}>
        <Formik
          enableReinitialize={true}
          initialValues={initialValues}
          onSubmit={(values) => {
            var rooms = _.cloneDeep(values);
            rooms = rooms.rooms.map((room) => ({
              ...room,
              children_ages: room.children_ages.map((age) => parseInt(age)),
            }));

            onFormChange(rooms);
            onCancel();
          }}
        >
          {({ values, setFieldValue, submitForm }) => (
            <Form className={classes.form}>
              <FieldArray name="rooms">
                {({ push, remove }) => (
                  <Fragment>
                    {max_rooms_allowed > 1 && (
                      <RoomsNumberController
                        max_rooms_allowed={max_rooms_allowed}
                        roomTemplate={initialValues.rooms[0]}
                        rooms={values.rooms}
                        onRemove={remove}
                        onAdd={push}
                      />
                    )}
                    <RoomSetupController
                      rooms={values?.rooms ?? []}
                      setFieldValue={setFieldValue}
                    />
                    <div className={classes.actions}>
                      <Button
                        id="cancelBtn"
                        text={t("cancel")}
                        revert={true}
                        onClick={(e: SyntheticEvent) => {
                          e.preventDefault();
                          onCancel();
                        }}
                      />
                      <Button
                        id="applyBtn"
                        text={t("apply")}
                        type="submit"
                        onClick={(e) => {
                          e.preventDefault();
                          submitForm();
                        }}
                      />
                    </div>
                  </Fragment>
                )}
              </FieldArray>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
}
PaxPickerModalACC.defaultProps = {};
PaxPickerModalACC.propTypes = {
  onCancel: PropTypes.func.isRequired,
};
export default PaxPickerModalACC;
